/**
 * Workout (session) template editor.
 *
 * A Workout template is a saved structure for a single session, organized
 * into the four standard blocks (Warm-up, Main, Accessory, Cool-down).
 * Each block holds a list of exercise references (picked from the exercise
 * library) with an optional per-row prescription override and notes.
 *
 * Save behavior:
 *   • Create: POST  /workout-templates           (with exercises[])
 *   • Edit:   PATCH /workout-templates/:id       (meta fields)
 *             PUT   /workout-templates/:id/exercises  (list)
 */

import { useState, type ReactNode } from 'react';
import { Icon } from '@k-os/ui';
import {
  useCreateWorkoutTemplate,
  usePatchWorkoutTemplate,
  useReplaceTemplateExercises,
  useExercises,
  type ExerciseBlock,
  type ExerciseType,
  type WorkoutTemplateDto,
} from '../../../api/health';
import { ExercisePicker, EXERCISE_BLOCK_META, EXERCISE_TYPE_META } from './atoms';
import styles from './workouts.module.css';

const BLOCK_ORDER: ExerciseBlock[] = ['warmup', 'main', 'accessory', 'cooldown'];
const TYPE_KEYS = Object.keys(EXERCISE_TYPE_META) as ExerciseType[];
const SET_BASED: ReadonlySet<ExerciseType> = new Set<ExerciseType>(['strength', 'plyometric', 'hold']);

interface DraftExercise {
  // Local id used for keying when rearranging — not persisted.
  key: string;
  exerciseId: string;
  exerciseName: string;
  exerciseType: ExerciseType;
  block: ExerciseBlock;
  prescription: string | null;
  notes: string;
  defaultSets: number | null;
}

function genKey(): string {
  return Math.random().toString(36).slice(2, 10);
}

export function WorkoutTemplateEditor({
  template,
  onCancel,
  onSaved,
}: {
  template?: WorkoutTemplateDto;
  onCancel: () => void;
  onSaved: (saved: WorkoutTemplateDto) => void;
}) {
  const isEdit = !!template;
  const create = useCreateWorkoutTemplate();
  const patch = usePatchWorkoutTemplate();
  const replaceExercises = useReplaceTemplateExercises();
  const exercisesQ = useExercises();

  const [name, setName] = useState(template?.name ?? '');
  const [description, setDescription] = useState(template?.description ?? '');
  const [type, setType] = useState<ExerciseType | string>(template?.type ?? 'strength');
  const [durationMin, setDurationMin] = useState<number | ''>(template?.durationMin ?? '');
  const [draft, setDraft] = useState<DraftExercise[]>(
    (template?.exercises ?? []).map((e) => ({
      key: genKey(),
      exerciseId: e.exerciseId,
      exerciseName: e.exercise?.name ?? '(missing)',
      exerciseType: (e.exercise?.type as ExerciseType) ?? 'strength',
      block: e.block,
      prescription: e.prescription,
      notes: e.notes ?? '',
      defaultSets: e.defaultSets ?? null,
    })),
  );

  const [pickerBlock, setPickerBlock] = useState<ExerciseBlock | null>(null);

  function addExerciseToBlock(block: ExerciseBlock, ex: { id: string; name: string; type: ExerciseType; prescription: string | null }): void {
    setDraft((prev) => [
      ...prev,
      {
        key: genKey(),
        exerciseId: ex.id,
        exerciseName: ex.name,
        exerciseType: ex.type,
        block,
        prescription: ex.prescription,
        notes: '',
        defaultSets: null,
      },
    ]);
    setPickerBlock(null);
  }
  function removeRow(key: string): void {
    setDraft((prev) => prev.filter((r) => r.key !== key));
  }
  function updateRow(key: string, patch: Partial<DraftExercise>): void {
    setDraft((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  }
  function move(key: string, dir: -1 | 1): void {
    setDraft((prev) => {
      const idx = prev.findIndex((r) => r.key === key);
      if (idx < 0) return prev;
      const j = idx + dir;
      if (j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[j]] = [next[j]!, next[idx]!];
      return next;
    });
  }
  function moveToBlock(key: string, targetBlock: ExerciseBlock): void {
    setDraft((prev) => {
      const item = prev.find((r) => r.key === key);
      if (!item) return prev;
      const withoutItem = prev.filter((r) => r.key !== key);
      const movedItem = { ...item, block: targetBlock };
      // Rebuild in canonical block order; moved item appended at end of target block.
      return BLOCK_ORDER.flatMap((b) => {
        const blockItems = withoutItem.filter((r) => r.block === b);
        return b === targetBlock ? [...blockItems, movedItem] : blockItems;
      });
    });
  }

  const ready = name.trim().length > 0 && draft.length > 0;

  async function save(): Promise<void> {
    const exercisesPayload = draft.map((r, i) => ({
      exerciseId: r.exerciseId,
      block: r.block,
      position: i + 1,
      prescription: r.prescription ?? null,
      notes: r.notes,
      defaultSets: r.defaultSets,
    }));

    if (isEdit && template) {
      // Meta first, then exercise list — replace endpoint is authoritative.
      await patch.mutateAsync({
        id: template.id,
        patch: {
          name: name.trim(),
          description,
          type,
          durationMin: durationMin === '' ? null : Number(durationMin),
        },
      });
      const res = await replaceExercises.mutateAsync({
        id: template.id,
        exercises: exercisesPayload,
      });
      onSaved(res.template);
    } else {
      const res = await create.mutateAsync({
        name: name.trim(),
        description,
        ...(typeof type === 'string' ? { type } : {}),
        durationMin: durationMin === '' ? null : Number(durationMin),
        exercises: exercisesPayload,
      });
      onSaved(res.template);
    }
  }

  const grouped = BLOCK_ORDER.map((b) => ({
    block: b,
    items: draft.filter((r) => r.block === b),
  }));

  return (
    <div className={styles.editor}>
      <div className={styles.crumb}>
        <span className={styles.crumbLink} onClick={onCancel}>
          Workouts · Library
        </span>
        <Icon name="chevron" size={11} />
        <span className={styles.crumbActive}>
          {isEdit ? 'Edit workout template' : 'New workout template'}
        </span>
      </div>
      <h2 className={styles.editorTitle}>
        {isEdit ? 'Edit workout template' : 'New workout template'}
      </h2>
      <div className={styles.editorIntro}>
        A workout template is a saved structure for one session — warm-up, main, accessory, and
        cool-down blocks each holding one or more exercises from your library. Start a session from
        it any day; the per-set tracker remembers your last weights and reps.
      </div>

      {/* Name */}
      <FieldLabel>Name</FieldLabel>
      <input
        autoFocus
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="e.g. Lower strength A · Upper strength · Easy 5km Z2"
        className={`${styles.editorInput} ${styles.editorInputLg}`}
      />

      {/* Description */}
      <FieldLabel optional>Description</FieldLabel>
      <textarea
        rows={2}
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="What's this template for? What's the intent?"
        className={styles.editorInput}
        style={{ minHeight: 60, resize: 'vertical' }}
      />

      {/* Type + duration row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <div>
          <FieldLabel>Type</FieldLabel>
          <div className={styles.editorChipRow}>
            {TYPE_KEYS.map((t) => (
              <button
                key={t}
                type="button"
                className={`${styles.filterChip} ${type === t ? styles.filterChipActive : ''}`}
                onClick={() => setType(t)}
              >
                <span
                  style={{ width: 7, height: 7, borderRadius: 2, background: EXERCISE_TYPE_META[t].color, marginRight: 5 }}
                />
                {EXERCISE_TYPE_META[t].name}
              </button>
            ))}
          </div>
        </div>
        <div>
          <FieldLabel optional>Target duration (min)</FieldLabel>
          <input
            type="number"
            min={0}
            value={durationMin}
            onChange={(e) => setDurationMin(e.target.value === '' ? '' : Number(e.target.value))}
            placeholder="55"
            className={styles.editorInput}
          />
        </div>
      </div>

      {/* Blocks */}
      <FieldLabel hint="add exercises from your library — one row per exercise">
        Structure
      </FieldLabel>
      {grouped.map(({ block, items }) => (
        <div key={block} className={styles.tmplBlock}>
          <div className={styles.tmplBlockHead}>
            <div>
              <div className={styles.tmplBlockTitle}>{EXERCISE_BLOCK_META[block].name}</div>
              <div className={styles.tmplBlockDesc}>{EXERCISE_BLOCK_META[block].desc}</div>
            </div>
            <span className={styles.tmplBlockCount}>
              {items.length} exercise{items.length !== 1 ? 's' : ''}
            </span>
          </div>
          {items.map((row, i) => {
            const blockIdx = BLOCK_ORDER.indexOf(row.block);
            const prevBlock = blockIdx > 0 ? BLOCK_ORDER[blockIdx - 1]! : null;
            const nextBlock = blockIdx < BLOCK_ORDER.length - 1 ? BLOCK_ORDER[blockIdx + 1]! : null;

            return (
              <div key={row.key} className={styles.tmplExRow}>
                <span style={{ width: 8, height: 8, borderRadius: 2, background: EXERCISE_TYPE_META[row.exerciseType]?.color }} />
                <div style={{ minWidth: 140 }}>
                  <div className={styles.tmplExName}>{row.exerciseName}</div>
                  <div className={styles.tmplExMeta}>{EXERCISE_TYPE_META[row.exerciseType]?.name}</div>
                </div>

                {SET_BASED.has(row.exerciseType) ? (
                  <div className={styles.tmplExSets}>
                    <span className={styles.tmplExSetsLabel}>Sets</span>
                    <input
                      type="number"
                      min={1}
                      max={20}
                      value={row.defaultSets ?? ''}
                      placeholder="3"
                      onChange={(e) =>
                        updateRow(row.key, {
                          defaultSets: e.target.value === '' ? null : Math.max(1, parseInt(e.target.value, 10) || 1),
                        })
                      }
                      className={styles.tmplExSetsInput}
                    />
                  </div>
                ) : (
                  <div className={styles.tmplExSetsSpacer} />
                )}

                <input
                  value={row.prescription ?? ''}
                  onChange={(e) => updateRow(row.key, { prescription: e.target.value || null })}
                  placeholder="Prescription (e.g. 4×6 @ 70%, 5km Z2, 3×45s)"
                  className={styles.tmplExPrescription}
                />

                <div className={styles.tmplExActions}>
                  <button type="button" className="kos-icon-btn" title="Move up"
                    onClick={() => move(row.key, -1)} disabled={i === 0}>
                    <Icon name="arrowUp" size={11} />
                  </button>
                  <button type="button" className="kos-icon-btn" title="Move down"
                    onClick={() => move(row.key, 1)} disabled={i === items.length - 1}>
                    <Icon name="arrowDown" size={11} />
                  </button>

                  {prevBlock && (
                    <button
                      type="button"
                      className={styles.tmplExMoveBtn}
                      title={`Move to ${EXERCISE_BLOCK_META[prevBlock].name}`}
                      onClick={() => moveToBlock(row.key, prevBlock)}
                    >
                      ← {EXERCISE_BLOCK_META[prevBlock].name}
                    </button>
                  )}
                  {nextBlock && (
                    <button
                      type="button"
                      className={styles.tmplExMoveBtn}
                      title={`Move to ${EXERCISE_BLOCK_META[nextBlock].name}`}
                      onClick={() => moveToBlock(row.key, nextBlock)}
                    >
                      → {EXERCISE_BLOCK_META[nextBlock].name}
                    </button>
                  )}

                  <button type="button" className="kos-icon-btn" title="Remove"
                    onClick={() => removeRow(row.key)}>
                    <Icon name="x" size={11} />
                  </button>
                </div>
              </div>
            );
          })}
          <button
            type="button"
            className={styles.tmplAddBtn}
            onClick={() => setPickerBlock(block)}
          >
            <Icon name="plus" size={11} /> Add to {EXERCISE_BLOCK_META[block].name}
          </button>
        </div>
      ))}

      {/* Picker */}
      {pickerBlock !== null && (
        <ExercisePicker
          exercises={exercisesQ.data?.exercises ?? []}
          mode="add"
          defaultBlock={pickerBlock}
          onPick={(ex) =>
            addExerciseToBlock(pickerBlock, {
              id: ex.id,
              name: ex.name,
              type: ex.type,
              prescription: ex.prescription ?? null,
            })
          }
          onClose={() => setPickerBlock(null)}
        />
      )}

      {/* Footer */}
      <div className={styles.editorFooter}>
        <button type="button" className="kos-btn" onClick={onCancel}>
          Cancel
        </button>
        <span className={styles.editorFooterRight}>
          <span className={styles.editorReady}>
            {ready ? (
              <span style={{ color: 'var(--accent)' }}>● Ready to save</span>
            ) : (
              <span style={{ color: 'var(--ink-4)' }}>○ Name + at least one exercise required</span>
            )}
          </span>
          <button
            type="button"
            className="kos-btn kos-btn-primary"
            disabled={!ready || create.isPending || patch.isPending || replaceExercises.isPending}
            onClick={() => void save()}
          >
            <Icon name="check" size={11} />{' '}
            {isEdit ? 'Save changes' : 'Create template'}
          </button>
        </span>
      </div>
    </div>
  );
}

function FieldLabel({
  children,
  optional,
  hint,
}: {
  children: ReactNode;
  optional?: boolean | undefined;
  hint?: string | undefined;
}) {
  return (
    <div className={styles.editorLabel}>
      <span className={styles.editorLabelText}>{children}</span>
      {optional && <span className={styles.editorLabelOptional}>OPTIONAL</span>}
      {hint && <span className={styles.editorLabelHint}>{hint}</span>}
    </div>
  );
}

